import React, { useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import { Container } from 'react-bootstrap'
import { useSearchParams } from 'react-router-dom'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import {
  AccountCustomWorkflowRunSummary,
  useLazyGetCustomWorkflowQuery,
  useLazyGetCustomWorkflowRunQuery,
  useListAccountCustomWorkflowRunsQuery,
  useListCustomWorkflowsQuery,
} from '../../store/customWorkflow/customWorkflows'
import { entityTag } from '../../components/CustomWorkflows/entityTags'
import { Modal } from '../../components/Modal/Modal'
import { ProvideInputModal } from '../../components/CustomWorkflows/ProvideInputModal'
import { RunResultView } from '../../components/CustomWorkflows/RunResultView'
import { SegmentedControl } from '../../components/SegmentedControl/SegmentedControl'
import { Spinner } from '../../components/Spinner/Spinner'
import { WORKFLOW_KINDS, type WorkflowKind } from '../../components/CustomWorkflows/workflowTags'
import { WorkflowsTabBar, WorkflowTab } from './Tabs/WorkflowsTabBar'
import { RUN_TABS, RunTabKey } from './Runs/runTabs'
import { clampPages } from './Runs/clampPages'
import { RunsTab } from './Runs/RunsTab'
import { useWorkflowsById, WORKFLOW_LOOKUP_SIZE } from './Runs/useWorkflowsById'
import { RunRowAction } from './Runs/WorkflowRunRow'
import { RunAreaFilter, RunWorkflowFilter } from './Runs/RunFilters'
import { readRunFilters, writeRunFilters, type RunFiltersValue } from './Runs/runFilterParams'
import { LibraryWorkflows } from './Library/LibraryWorkflows'
import { buildCatalog, NO_LIBRARY_FILTER, type LibraryFilter } from './Library/libraryCatalog'
import styles from './Workflows.module.scss'

const asKind = (key: string): WorkflowKind | 'all' => WORKFLOW_KINDS.find((kind) => kind === key) ?? 'all'

const PAGE_SIZE = 12
const TAB_PARAM = 'tab'
const LIBRARY_TAB: WorkflowTab = 'library'
const NEEDS_ACTION_TAB: WorkflowTab = 'needs-action'
const VALID_TABS: WorkflowTab[] = [NEEDS_ACTION_TAB, 'running', 'completed', LIBRARY_TAB]
const FIRST_PAGES: Record<RunTabKey, number> = { 'needs-action': 0, running: 0, completed: 0 }

const pickDefaultTab = (needsActionCount: number | null, needsActionError: boolean): WorkflowTab | null => {
  if (needsActionError) {
    return NEEDS_ACTION_TAB
  }
  if (needsActionCount === null) {
    return null
  }
  return needsActionCount === 0 ? LIBRARY_TAB : NEEDS_ACTION_TAB
}

export const WorkflowsLanding: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)
  const workflowsById = useWorkflowsById(accountId)
  const [searchParams, setSearchParams] = useSearchParams()
  const [pages, setPages] = useState<Record<RunTabKey, number>>(FIRST_PAGES)
  const [mountedAt] = useState(Date.now)
  const [inputRun, setInputRun] = useState<AccountCustomWorkflowRunSummary | null>(null)
  const [selectedRun, setSelectedRun] = useState<AccountCustomWorkflowRunSummary | null>(null)
  const [triggerGetRun, { data: runDetail, isFetching: runDetailLoading, isError: runDetailError }] = useLazyGetCustomWorkflowRunQuery()
  const [triggerGetWorkflow, { data: workflowDetail }] = useLazyGetCustomWorkflowQuery()

  const rawTab = searchParams.get(TAB_PARAM)
  const requestedTab = VALID_TABS.find((tab) => tab === rawTab)
  const setActiveTab = (tab: WorkflowTab) => {
    const next = new URLSearchParams(searchParams)
    next.set(TAB_PARAM, tab)
    setSearchParams(next, { replace: true })
  }

  const filters = readRunFilters(searchParams)
  const setFilters = (next: RunFiltersValue) => {
    setSearchParams(writeRunFilters(searchParams, next), { replace: true })
  }
  const filterParams = { ...(filters.workflowId ? { workflowId: filters.workflowId } : {}), ...(filters.entity ? { scope: entityTag(filters.entity) } : {}) }

  const filterKey = `${filters.workflowId ?? ''}|${filters.entity ?? ''}`
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey)
  const filterChanged = prevFilterKey !== filterKey
  if (filterChanged) {
    setPrevFilterKey(filterKey)
    setPages(FIRST_PAGES)
  }
  const activePages = filterChanged ? FIRST_PAGES : pages

  const runsBaseOptions = { skip: !accountId, skipPollingIfUnfocused: true }
  const activeRunsOptions = { ...runsBaseOptions, pollingInterval: 10000 }
  const needsActionOptions = { ...activeRunsOptions, refetchOnMountOrArgChange: true }
  const statusFor = (key: RunTabKey) => RUN_TABS.find((t) => t.key === key)!.status
  const needsAction = useListAccountCustomWorkflowRunsQuery(
    { accountId, status: statusFor('needs-action'), page: activePages['needs-action'], size: PAGE_SIZE, ...filterParams },
    needsActionOptions,
  )
  const running = useListAccountCustomWorkflowRunsQuery(
    { accountId, status: statusFor('running'), excludeSubRuns: true, page: activePages.running, size: PAGE_SIZE, ...filterParams },
    activeRunsOptions,
  )
  const completed = useListAccountCustomWorkflowRunsQuery(
    { accountId, status: statusFor('completed'), excludeSubRuns: true, page: activePages.completed, size: PAGE_SIZE, ...filterParams },
    runsBaseOptions,
  )
  const byKey: Record<RunTabKey, typeof needsAction> = { 'needs-action': needsAction, running, completed }

  const pageFor = (q: typeof needsAction) => q.currentData
  const isAwaitingRuns = (q: typeof needsAction) => q.isLoading || (q.isFetching && q.currentData === undefined)

  const totalPages: Record<RunTabKey, number> = {
    'needs-action': pageFor(needsAction)?.page.totalPages ?? 0,
    running: pageFor(running)?.page.totalPages ?? 0,
    completed: pageFor(completed)?.page.totalPages ?? 0,
  }
  const [prevTotalPages, setPrevTotalPages] = useState<Record<RunTabKey, number>>(totalPages)
  if (
    prevTotalPages['needs-action'] !== totalPages['needs-action'] ||
    prevTotalPages.running !== totalPages.running ||
    prevTotalPages.completed !== totalPages.completed
  ) {
    setPrevTotalPages(totalPages)
    setPages((prev) => clampPages(prev, totalPages))
  }

  const countFor = (q: typeof needsAction) => (isAwaitingRuns(q) ? null : (pageFor(q)?.page.totalElements ?? 0))
  const counts = {
    'needs-action': countFor(needsAction),
    running: countFor(running),
    completed: countFor(completed),
  }

  const needsActionFresh = (needsAction.fulfilledTimeStamp ?? 0) >= mountedAt
  const defaultTab = pickDefaultTab(needsActionFresh ? counts['needs-action'] : null, needsAction.isError)
  const activeTab: WorkflowTab | null = requestedTab ?? defaultTab
  const isLibrary = activeTab === LIBRARY_TAB

  const normalisedParams = writeRunFilters(searchParams, filters)
  if (!requestedTab && defaultTab) {
    normalisedParams.set(TAB_PARAM, defaultTab)
  }
  const normalisedSearch = normalisedParams.toString()
  useEffect(() => {
    if (normalisedSearch !== searchParams.toString()) {
      setSearchParams(normalisedParams, { replace: true })
    }
  }, [normalisedSearch, normalisedParams, searchParams, setSearchParams])

  const {
    data: libraryWorkflows,
    isLoading: libraryWorkflowsLoading,
    isError: libraryWorkflowsError,
  } = useListCustomWorkflowsQuery({ accountId, size: WORKFLOW_LOOKUP_SIZE }, { skip: !isLibrary })
  const [libraryFilter, setLibraryFilter] = useState<LibraryFilter>(NO_LIBRARY_FILTER)
  const libraryCatalog = useMemo(() => buildCatalog(libraryWorkflows ?? [], libraryFilter), [libraryWorkflows, libraryFilter])

  const runKey: RunTabKey | null = RUN_TABS.map((t) => t.key).find((key) => key === activeTab) ?? null
  const runDef = runKey ? RUN_TABS.find((t) => t.key === runKey)! : null
  const runQuery = runKey ? byKey[runKey] : null

  const onViewResult = (run: AccountCustomWorkflowRunSummary) => {
    setSelectedRun(run)
    if (accountId) {
      void triggerGetRun({ accountId, workflowId: run.workflowId, runId: run.id })
      void triggerGetWorkflow({ accountId, workflowId: run.workflowId })
    }
  }

  const actionForTab = (key: RunTabKey): ((run: AccountCustomWorkflowRunSummary) => RunRowAction | undefined) | undefined => {
    if (key === 'needs-action') {
      return (run) => ({ label: 'Provide input', variant: 'primary', onClick: () => setInputRun(run) })
    }
    if (key === 'completed') {
      return (run) => ({ label: 'View result', variant: 'secondaryGhostButton', onClick: () => onViewResult(run) })
    }
    return undefined
  }

  return (
    <Container fluid className='p-4 d-flex flex-column align-items-center truly-full-size-content zocks-bg-color-gray-100 min-vh-100'>
      <div className={classNames('d-flex flex-column zocks-gap-2 w-100', styles.pageContent)}>
        <h3>Agents</h3>
        {activeTab ? (
          <>
            <div className='d-flex flex-wrap justify-content-between align-items-center zocks-gap-2'>
              <WorkflowsTabBar activeTab={activeTab} counts={counts} onSelect={setActiveTab} />
              {runKey ? (
                <RunWorkflowFilter accountId={accountId} value={filters} onChange={setFilters} />
              ) : isLibrary && libraryCatalog.kinds.length > 2 ? (
                <SegmentedControl
                  options={libraryCatalog.kinds.map((option) => ({ key: option.key, label: option.label, count: option.count }))}
                  activeKey={libraryCatalog.selected.kind}
                  ariaLabel='Filter workflows by type'
                  surface='gray'
                  onSelect={(key) => setLibraryFilter({ ...libraryCatalog.selected, kind: asKind(key) })}
                />
              ) : null}
            </div>
            {runKey ? <RunAreaFilter value={filters} onChange={setFilters} /> : null}
            <div className='pt-2'>
              {isLibrary ? (
                <LibraryWorkflows
                  accountId={accountId}
                  active={isLibrary}
                  catalog={libraryCatalog}
                  isLoading={libraryWorkflowsLoading}
                  isError={libraryWorkflowsError}
                  onFilterChange={setLibraryFilter}
                />
              ) : runKey && runDef && runQuery ? (
                <>
                  <RunsTab
                    key={runKey}
                    accountId={accountId}
                    runs={pageFor(runQuery)?.data ?? []}
                    workflowsById={workflowsById}
                    isLoading={isAwaitingRuns(runQuery)}
                    isError={runQuery.isError}
                    page={activePages[runKey]}
                    totalPages={pageFor(runQuery)?.page.totalPages ?? 0}
                    onPageChange={(page) => setPages((prev) => ({ ...prev, [runKey]: page }))}
                    emptyMessage={runDef.emptyMessage}
                    actionFor={actionForTab(runKey)}
                    showSubRunBadge={runKey === 'needs-action'}
                    groupChildRuns={runKey !== 'needs-action'}
                  />
                </>
              ) : null}
            </div>
          </>
        ) : (
          <div className='d-flex justify-content-center py-5'>
            <Spinner />
          </div>
        )}
      </div>

      {inputRun && accountId ? (
        <ProvideInputModal
          accountId={accountId}
          workflowId={inputRun.workflowId}
          runId={inputRun.id}
          title={inputRun.workflowName || 'Provide input'}
          show={!!inputRun}
          onHide={() => setInputRun(null)}
        />
      ) : null}

      <Modal
        size='xl'
        scrollable
        show={!!selectedRun}
        onHide={() => setSelectedRun(null)}
        bodyClassName='pb-4'
        header={<h5 className='mb-0'>Workflow run</h5>}
        body={
          selectedRun ? (
            <RunResultView
              accountId={accountId}
              workflowId={selectedRun.workflowId}
              runId={selectedRun.id}
              workflowName={selectedRun.workflowName || selectedRun.workflowId}
              status={selectedRun.status}
              createdAt={selectedRun.createdAt}
              finishedAt={selectedRun.finishedAt}
              elapsedTime={selectedRun.elapsedTime}
              workflow={workflowDetail?.id === selectedRun.workflowId ? workflowDetail : workflowsById.get(selectedRun.workflowId)}
              detail={runDetail}
              isLoading={runDetailLoading}
              isError={runDetailError}
            />
          ) : null
        }
      />
    </Container>
  )
}

export default WorkflowsLanding
